import { Plus } from "@phosphor-icons/react";
import { cn } from "../../@shadcn/lib/utils";
import PrimaryButton from "../../buttons/PrimaryButton";
import GiftCardAppliedRow from "../components/GiftCardAppliedRow";
import GiftCardSavingsStrip from "../components/GiftCardSavingsStrip";
import GiftCardSuccessStrip from "../components/GiftCardSuccessStrip";
import { useGiftCardContext } from "../context/GiftProvider";
import { hexToRgba } from "../../utils/colorUtils";
import { defaultFormatAmount } from "../commons/constants";

const DEFAULT_ACCENT_COLOR = "#432a2e";
const DEFAULT_TITLE = "Your Gift Cards";
const DEFAULT_COUNT_LABEL = "Added";
const DEFAULT_ADD_ANOTHER_LABEL = "Add another";
const DEFAULT_SUBMIT_LABEL = "Done";
const DEFAULT_FULLY_COVERED_TEXT = "Your order is fully covered by gift cards!";
const DEFAULT_FULLY_COVERED_MESSAGE = "No additional payment needed!"

export interface GiftCardAppliedListProps {
  formatAmount?: (value: number) => string;
  savingsAmount?: number;
  isFullyCovered?: boolean;
  onSubmit: () => void;
  accentColor?: string;
  title?: string;
  countLabel?: string;
  addAnotherLabel?: string;
  submitLabel?: string;
  removeCardLabel?: string;
}

const GiftCardAppliedList = ({
  formatAmount = defaultFormatAmount,
  savingsAmount,
  isFullyCovered = false,
  onSubmit,
  accentColor = DEFAULT_ACCENT_COLOR,
  title = DEFAULT_TITLE,
  countLabel = DEFAULT_COUNT_LABEL,
  addAnotherLabel = DEFAULT_ADD_ANOTHER_LABEL,
  submitLabel = DEFAULT_SUBMIT_LABEL,
  removeCardLabel,
}: GiftCardAppliedListProps) => {
  const {
    appliedCards,
    maxUsage,
    giftCards,
    setActiveStep,
    resetGiftCards,
    deleteAppliedCard,
    getDeleteState,
    canDeleteCard,
  } = useGiftCardContext();

  const canAddAnother = appliedCards.length < maxUsage && !isFullyCovered;
  // The card just applied is still sitting on the provider cards — its code, its balance, its
  // errors. Clear them before the entry screen so the next card starts blank.
  const handleAddAnother = () => {
    resetGiftCards();
    setActiveStep("CARD_ENTRY");
  };
  // A card the host handed over without an amount contributes nothing rather than poisoning the
  // total: one NaN would read as falsy and take the whole savings strip down with it.
  const savings =
    savingsAmount ?? appliedCards.reduce((total, card) => total + (card.amount ?? 0), 0);

  return (
    <div className="ui-flex ui-min-h-0 ui-w-full ui-flex-col ui-rounded-t-2xl ui-bg-white">
      <div className="ui-flex ui-min-h-[64px] ui-w-full ui-shrink-0 ui-items-center ui-justify-between ui-gap-3 ui-px-5 ui-py-4">
        <p className="ui-text-text-lg-semibold ui-text-zinc-800">{title}</p>
        <p className="ui-shrink-0 ui-px-3 ui-text-eyebrow-xs ui-uppercase ui-text-black ui-opacity-50">
          {appliedCards.length}/{maxUsage} {countLabel}
        </p>
      </div>

      <div className="ui-flex ui-min-h-0 ui-w-full ui-flex-1 ui-flex-col ui-overflow-y-auto ui-overscroll-contain ui-px-3 ui-py-2">
        {appliedCards.map((card, index) => {
          const display = card.provider ? giftCards[card.provider]?.display_config : undefined;
          const { loading, error } = getDeleteState(card.transaction_id);
          const isLast = index === appliedCards.length - 1;

          return (
            <div
              key={card.transaction_id}
              className={cn(
                "ui-relative",
                !isLast &&
                  "after:ui-content-[''] after:ui-absolute after:ui-left-3 after:ui-right-3 after:ui-bottom-0 after:ui-h-px after:ui-bg-black/4",
              )}>
              <GiftCardAppliedRow
                card={card}
                iconUrl={display?.iconUrl}
                label={display?.label}
                formatAmount={formatAmount}
                onRemove={deleteAppliedCard}
                canRemove={canDeleteCard}
                isDeleting={loading}
                error={error}
                removeLabel={removeCardLabel}
              />
            </div>
          );
        })}

        {canAddAnother &&  (
          <button
            type="button"
            onClick={handleAddAnother}
            className="ui-flex ui-w-full ui-items-center ui-gap-4 ui-rounded-xl ui-p-3 ui-text-left hover:ui-bg-black/4">
            <span
              className="ui-flex ui-size-10 ui-shrink-0 ui-items-center ui-justify-center ui-rounded-full ui-text-zinc-800"
              style={{ backgroundColor: hexToRgba(accentColor, 0.06) }}>
              <Plus size={18} weight="bold" />
            </span>
            <span className="ui-text-text-md-medium ui-text-zinc-800 ui-opacity-70">
              {addAnotherLabel}
            </span>
          </button>
        )}
      </div>

      <div className="ui-mt-auto ui-flex ui-w-full ui-shrink-0 ui-flex-col">
        {isFullyCovered ? (
          <GiftCardSuccessStrip
            message={DEFAULT_FULLY_COVERED_TEXT}
            subtext={DEFAULT_FULLY_COVERED_MESSAGE}
          />
        ) : (
          Boolean(savings) && <GiftCardSavingsStrip label={`Saved ${formatAmount(savings)}!`} />
        )}

        <div className="ui-w-full ui-bg-white ui-px-4 ui-pb-6 ui-shadow-[0_-4px_12px_4px_#FFF]">
          <PrimaryButton
            height="ui-h-[52px]"
            buttonText={submitLabel}
            customization={{ backgroundColor: accentColor, hoverBackgroundColor: accentColor }}
            onClick={onSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default GiftCardAppliedList;
